// ML-UP-01 — the Media Library's upload behaviour, as a hook.
//
// Kept out of pages/MediaLibrary/index.jsx deliberately: that container was
// already the page's largest function, and folding the chooser + validation +
// sequencing + toast reporting into it pushed it (and a new `onFilesChosen`)
// over the repo's complexity thresholds. The decision rules live in
// upload.logic.js (pure, unit-tested); this hook is the thin I/O shell that
// wires them to the file input and the react-query cache.
import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import { handleFileUpload } from "../../utils/fileUpload";
import {
  getMediaLibraryUploadPath,
  partitionUploadFiles,
  rejectionMessage,
  uploadAll,
  uploadSummaryMessage,
} from "./upload.logic";

// The logged-in user id, read the same lightweight inline way utils/fileUpload
// does — so the upload key is namespaced per user without pulling the heavy
// helpers barrel into this route's chunk.
function readUserId() {
  try {
    const parsed = JSON.parse(localStorage.getItem("keyCloak") || "{}");
    return parsed && parsed.user && parsed.user.id != null ? parsed.user.id : null;
  } catch (e) {
    return null;
  }
}

/**
 * @param {() => void} onUploaded called once after at least one file landed, so
 *   the caller can invalidate/refetch its media + storage queries.
 */
export default function useMediaLibraryUpload(onUploaded) {
  const fileInputRef = useRef(null);
  // Synchronous latch: react state flips only on the NEXT render, so it cannot
  // stop a same-tick second submit (fast double-click / Enter key-repeat). This
  // ref can. Mirrors the delete path's `deleteInFlight` guard.
  const inFlight = useRef(false);
  const [uploading, setUploading] = useState(false);

  const openFilePicker = useCallback(() => {
    if (inFlight.current) return; // don't stack chooser dialogs mid-upload
    if (fileInputRef.current) fileInputRef.current.click();
  }, []);

  const onFilesChosen = useCallback(
    async (event) => {
      const input = event.target;
      const { accepted, tooLarge, wrongType } = partitionUploadFiles(input.files);
      // Reset immediately so re-choosing the SAME file fires `change` again.
      input.value = "";

      const refused = rejectionMessage({ tooLarge, wrongType });
      if (refused) toast.error(refused);
      // Cancelled chooser, or everything refused: nothing left to do.
      if (accepted.length === 0 || inFlight.current) return;

      inFlight.current = true;
      setUploading(true);

      const uploadPath = getMediaLibraryUploadPath({ user_id: readUserId() });
      // wheel_id/segment_id are null by design — a Media Library upload belongs
      // to no wheel; moderation handles the wheel-less case by user + URL.
      const { ok, failed } = await uploadAll(accepted, (file) =>
        handleFileUpload(file, uploadPath, null, null)
      );

      inFlight.current = false;
      setUploading(false);

      const summary = uploadSummaryMessage({ ok, failed });
      if (summary) (ok ? toast.success : toast.error)(summary);
      if (ok > 0 && onUploaded) onUploaded();
    },
    [onUploaded]
  );

  return { fileInputRef, uploading, openFilePicker, onFilesChosen };
}
